/**
 * A ficha preenchida fora da tela: impressa e corrigida (trilha T5a).
 *
 * As regras da correção são as da `alterar_respostas_da_ficha` da `0086`,
 * ditas antes de ir ao banco.
 */

import type { CampoDeFicha, DadosDaClinica, RespostaDaFicha, UnidadeDaClinica } from './tipos';

/** O que o cabeçalho da folha leva. */
export interface CabecalhoDaClinica {
    nome: string;
    razaoSocial?: string;
    documento?: string;
    email?: string;
    telefone?: string;
    /**
     * Só quando a clínica tem UMA unidade. Com várias, a ficha não guarda de
     * qual unidade veio — e endereço escolhido a esmo em documento clínico é
     * informação errada com cara de oficial.
     */
    endereco?: string;
    unidades: number;
}

export const MOTIVO_MINIMO_DA_CORRECAO = 5;

export function enderecoDaUnidade(u: UnidadeDaClinica): string {
    const rua = [u.rua ? `${u.rua}${u.numero ? `, ${u.numero}` : ''}` : '', u.complemento].filter(Boolean).join(' — ');
    const cidade = [u.bairro, u.cidade ? `${u.cidade}${u.estado ? `/${u.estado}` : ''}` : ''].filter(Boolean).join(', ');
    return [rua, cidade, u.cep ? `CEP ${u.cep}` : ''].filter(Boolean).join(' · ');
}

export function cabecalhoDe(clinica: DadosDaClinica, unidades: UnidadeDaClinica[]): CabecalhoDaClinica {
    const unica = unidades.length === 1 ? unidades[0] : undefined;
    const endereco = unica ? enderecoDaUnidade(unica) : '';
    return {
        nome: clinica.nome,
        unidades: unidades.length,
        ...(clinica.razaoSocial ? { razaoSocial: clinica.razaoSocial } : {}),
        ...(clinica.documento ? { documento: clinica.documento } : {}),
        ...(clinica.email ? { email: clinica.email } : {}),
        ...(unica?.telefone ? { telefone: unica.telefone } : {}),
        ...(endereco ? { endereco } : {}),
    };
}

export function respostaVazia(valor: RespostaDaFicha | null): boolean {
    return valor === undefined || valor === null || valor === '' || (Array.isArray(valor) && valor.length === 0);
}

/** As respostas sem as vazias, em ordem de chave — é assim que se compara "mudou". */
export function respostasLimpas(respostas: Record<string, RespostaDaFicha>): Record<string, RespostaDaFicha> {
    return Object.fromEntries(
        Object.entries(respostas)
            .filter(([, valor]) => !respostaVazia(valor))
            .sort(([a], [b]) => a.localeCompare(b)),
    );
}

export function problemasDaAlteracao(
    campos: CampoDeFicha[],
    antes: Record<string, RespostaDaFicha>,
    depois: Record<string, RespostaDaFicha>,
    motivo: string,
): string[] {
    const problemas: string[] = [];
    const uids = new Set(campos.map((c) => c.uid));
    if (Object.keys(respostasLimpas(depois)).some((uid) => !uids.has(uid))) problemas.push('Há resposta para campo que a ficha não tem.');
    const faltando = campos.filter((c) => c.obrigatorio && respostaVazia(depois[c.uid]));
    if (faltando.length > 0) problemas.push(`Preencha: ${faltando.map((c) => c.rotulo).join(', ')}.`);
    if (motivo.trim().length < MOTIVO_MINIMO_DA_CORRECAO) problemas.push('Diga o motivo da correção.');
    if (JSON.stringify(respostasLimpas(antes)) === JSON.stringify(respostasLimpas(depois))) problemas.push('Nada mudou nas respostas.');
    return problemas;
}
